import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "@/lib/staticHead"; // must run before the first render: remembers the HTML shell's <title>
import "@/index.css";
import App from "@/App";

// No data-fetching library provider here on purpose: the app fetches with the small `api`
// helper (lib/api.js) and nothing used @tanstack/react-query hooks, so the provider only added
// ~12 KB (gzip) to the main bundle that every route — including the paid-search landing
// page — had to download before first paint.

/*
 * Every public route arrives as real HTML (#root[data-prerender="full"], rendered in Node by
 * seo/ssr.js), so the page is already painted when this bundle runs: hydrate it instead of
 * re-rendering it. createRoot is only the fallback for a document without pre-rendered markup
 * (e.g. /admin, or an unknown route served as the plain SPA shell).
 *
 * window.__hydrateErrors collects every hydration mismatch React recovers from (and any React
 * console error containing "hydrat") so QA can assert it is empty — see the Phase 59 report.
 *
 * Preview (dev server) only: Emergent's visual-edits babel plugin stamps x-file-name / x-dynamic /
 * x-source-* attributes on every element. Their values depend on babel-loader's disk cache and on
 * compile order, so the Node pre-render cannot reproduce them exactly and React reports attribute
 * diffs that touch ONLY those x-* attributes. They are dev tooling, never shipped to production
 * (craco build has no such plugin), and React leaves the DOM untouched for them — so they are kept
 * apart in window.__hydrateToolingWarnings and summarised in one console.info line instead of the
 * full React error. Any diff involving a real attribute still lands in window.__hydrateErrors and
 * is logged verbatim. Nothing here is user-facing.
 */
const container = document.getElementById("root");
// Static hosting serves build/index.html (the pre-rendered HOMEPAGE) as the SPA fallback for every path
// without its own file (/admin, /estimate/<id>, unknown URLs). Hydrating homepage markup against a
// different route would mismatch wholesale, so only hydrate when the markup was rendered for this path;
// otherwise drop the stale markup and let the client render the route from scratch.
const normalizePath = (p) => (p && p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p || "/");
const prerenderRoute = container.getAttribute("data-prerender-route");
const routeMatches = !prerenderRoute || normalizePath(prerenderRoute) === normalizePath(window.location.pathname);
const prerendered = container.hasAttribute("data-prerender") && !!container.firstElementChild && routeMatches;
if (!prerendered && container.firstElementChild) {
  container.replaceChildren();
  container.removeAttribute("data-prerender");
}
window.__hydrateErrors = window.__hydrateErrors || [];
window.__hydrateToolingWarnings = window.__hydrateToolingWarnings || [];
const IS_DEV_SERVER = process.env.NODE_ENV !== "production";
// Diff lines React prints look like `+   x-dynamic="false"` / `-   x-dynamic="true"`.
const DIFF_LINE_RE = /^\s*[+-]\s+([A-Za-z_:][-\w:.]*)(?:=|$)/;
const onlyVisualEditsAttributes = (text) => {
  const names = [];
  text.split("\n").forEach((line) => {
    const m = DIFF_LINE_RE.exec(line);
    if (m) names.push(m[1]);
  });
  return names.length > 0 && names.every((n) => n.startsWith("x-"));
};
const recordHydrateError = (err) => {
  const msg = err && err.message ? err.message : String(err);
  window.__hydrateErrors.push(msg);
};
// React reports hydration mismatches through console.error (not onRecoverableError) in some paths.
const origConsoleError = console.error;
console.error = (...args) => {
  try {
    const text = args.map((a) => (a && a.message) || String(a)).join(" ");
    if (/hydrat/i.test(text)) {
      if (IS_DEV_SERVER && onlyVisualEditsAttributes(text)) {
        window.__hydrateToolingWarnings.push(text.slice(0, 2000));
        console.info(`[hydration] preview-only visual-edits x-* attribute diff ignored (${window.__hydrateToolingWarnings.length}); not present in the production build`);
        return;
      }
      window.__hydrateErrors.push(text.slice(0, 500));
    }
  } catch (e) { /* never break logging */ }
  origConsoleError.apply(console, args);
};

const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

const mount = () => {
  if (prerendered) {
    hydrateRoot(container, app, { onRecoverableError: recordHydrateError });
  } else {
    createRoot(container).render(app);
  }
};

// Yield one frame first so the browser paints the pre-rendered HTML (and the hero) before React
// starts hydrating — hydration is main-thread work that would otherwise sit between first byte
// and first paint on slow phones.
if (prerendered && typeof requestAnimationFrame === "function") {
  requestAnimationFrame(() => setTimeout(mount, 0));
} else {
  mount();
}
